// @ts-nocheck
import { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, Users, Phone } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { CustomerStatsCard } from '../components/Dashboard/CustomerStatsCard'
import { CallStatsCard } from '../components/Dashboard/CallStatsCard'
import { CustomerTrendChart } from '../components/Dashboard/CustomerTrendChart'
import { CallTrendChart } from '../components/Dashboard/CallTrendChart'
import { CustomerTagDistributionChart } from '../components/Dashboard/CustomerTagDistributionChart'
import { TagCloudChart } from '../components/Dashboard/TagCloudChart'
import { logger } from '../utils/logger'

export default function StatisticsPage() {
  const user = useAuthStore((state) => state.user)
  const api = useApi()
  
  const [activeTab, setActiveTab] = useState('overview') // overview, calls, trends
  const [period, setPeriod] = useState('30d')
  const [customerStats, setCustomerStats] = useState(null)
  const [callStats, setCallStats] = useState(null)
  const [loading, setLoading] = useState(true)

  // 統計データを取得
  useEffect(() => {
    if (!user?.organizationId) {
      return
    }

    const fetchStats = async () => {
      setLoading(true)
      try {
        const [customerData, callData] = await Promise.all([
          api.get(`/api/stats/customers?period=${period}`),
          api.get(`/api/stats/calls?period=${period}`)
        ])
        setCustomerStats(customerData)
        setCallStats(callData)
      } catch (err) {
        logger.error('統計取得エラー:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [api, user?.organizationId, period])

  const tabs = [
    { id: 'overview', label: '概要', icon: BarChart3 },
    { id: 'calls', label: '通話分析', icon: Phone },
    { id: 'trends', label: 'トレンド', icon: TrendingUp },
  ]

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              統計・レポート
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              詳細な統計データと分析レポート
            </p>
          </div>
          
          {/* 期間セレクタ */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">過去7日間</option>
            <option value="30d">過去30日間</option>
            <option value="90d">過去90日間</option>
            <option value="1y">過去1年</option>
          </select>
        </div>

        {/* タブナビゲーション */}
        <div className="flex gap-2 mt-6 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* コンテンツエリア */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 dark:text-gray-400 mt-4">統計データを読み込み中...</p>
        </div>
      ) : (
        <>
          {/* 概要タブ */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* KPIサマリーカード */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <CustomerStatsCard stats={customerStats} />
                <CallStatsCard stats={callStats} />
              </div>

              {/* 主要グラフ */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomerTrendChart data={customerStats?.trend} period={period} />
                <CallTrendChart data={callStats?.callTrend} period={period} />
              </div>
            </div>
          )}

          {/* 通話分析タブ */}
          {activeTab === 'calls' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <CallStatsCard stats={callStats} />
              </div>
              <CallTrendChart data={callStats?.callTrend} period={period} />
            </div>
          )}

          {/* トレンドタブ */}
          {activeTab === 'trends' && (
            <div className="space-y-6">
              {/* トレンドチャート */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomerTrendChart data={customerStats?.trend} period={period} />
                <CallTrendChart data={callStats?.callTrend} period={period} />
              </div>

              {/* 詳細分析 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CustomerTagDistributionChart data={customerStats?.customerTagDistribution} />
                <TagCloudChart data={callStats?.tagStats} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
